/**
 * Unified Order Status Color Map
 * 
 * Single source of truth for all status-based coloring across the app.
 * Components like StatusBadge, OrderStatusRail, and any future status
 * displays should reference this map instead of defining their own colors.
 * 
 * Colors use CSS custom properties from the design system (index.css).
 * HSL values are provided for inline-style usage (StatusBadge).
 */

import { ORDER_STATUS } from "@/constants/orderConstants";

export interface StatusColor {
  /** HSL color string for inline styles, e.g. "231, 75%, 58%" */
  hsl: string;
  /** Tailwind bg class for class-based usage */
  bgClass: string;
}

/**
 * Canonical status color map.
 * 
 * Design decisions:
 * - New: Brand primary blue (trust, attention)
 * - Awaiting Payment: Warning amber (needs action)
 * - Completed/Ready: Success emerald (positive terminal for prep)
 * - Out for Delivery: Orange (in-transit, energy)
 * - Delivered: Green (positive terminal)
 * - Delivery Failed: Destructive red (needs resolution)
 * - Cancelled: Muted gray (inactive, no action needed)
 */
export const STATUS_COLORS: Record<string, StatusColor> = {
  [ORDER_STATUS.NEW]: {
    hsl: "231, 75%, 58%",
    bgClass: "bg-primary",
  },
  [ORDER_STATUS.AWAITING_PAYMENT]: {
    hsl: "38, 92%, 50%",
    bgClass: "bg-[hsl(var(--warning))]",
  },
  [ORDER_STATUS.COMPLETED]: {
    hsl: "158, 64%, 52%",
    bgClass: "bg-[hsl(var(--success))]",
  },
  // "Ready" is a UI alias for Completed in delivery contexts
  'Ready': {
    hsl: "158, 64%, 52%",
    bgClass: "bg-[hsl(var(--success))]",
  },
  [ORDER_STATUS.OUT_FOR_DELIVERY]: {
    hsl: "25, 95%, 53%",
    bgClass: "bg-orange-500",
  },
  [ORDER_STATUS.DELIVERED]: {
    hsl: "142, 71%, 45%",
    bgClass: "bg-green-500",
  },
  [ORDER_STATUS.DELIVERY_FAILED]: {
    hsl: "0, 84%, 60%",
    bgClass: "bg-destructive",
  },
  [ORDER_STATUS.CANCELLED]: {
    hsl: "220, 9%, 46%",
    bgClass: "bg-muted-foreground/30",
  },
  // Legacy backward compat
  'Processing': {
    hsl: "38, 92%, 50%",
    bgClass: "bg-[hsl(var(--warning))]",
  },
};

/** Default fallback color for unknown statuses */
export const STATUS_COLOR_FALLBACK: StatusColor = {
  hsl: "231, 75%, 58%",
  bgClass: "bg-primary",
};

/** Get status color with fallback */
export function getStatusColor(status: string): StatusColor {
  return STATUS_COLORS[status] || STATUS_COLOR_FALLBACK;
}
